import { graphqlRequest } from "./graphql";

export interface SharedSubOrderRow {
  id: string;
  created_at: string;
  allocated_at: string | null;
  payment_id: string | null;
  plan_id: string | null;
  price: number;
  short_id: string;
  status: string;
  whatsub_plan: {
    plan_name: string;
    whatsub_service: { service_name: string; image_url: string };
  } | null;
}

export interface SharedSubOrderFilters {
  userId: string;
  statuses?: string[];
  createdFrom?: string;
  createdTo?: string;
  search?: string;
}

function buildInnerWhere(filters: SharedSubOrderFilters): Record<string, unknown> {
  const where: Record<string, unknown> = {};
  if (filters.statuses && filters.statuses.length > 0) {
    where["status"] = { _in: filters.statuses };
  }
  const createdAt: Record<string, string> = {};
  if (filters.createdFrom) createdAt["_gte"] = filters.createdFrom;
  if (filters.createdTo) createdAt["_lte"] = filters.createdTo;
  if (Object.keys(createdAt).length > 0) where["created_at"] = createdAt;
  if (filters.search) {
    where["_or"] = [
      { short_id: { _ilike: `%${filters.search}%` } },
      { whatsub_plan: { plan_name: { _ilike: `%${filters.search}%` } } },
      { whatsub_plan: { whatsub_service: { service_name: { _ilike: `%${filters.search}%` } } } },
    ];
  }
  return where;
}

interface GetOrdersResponse {
  whatsub_b2b_shared_subscriptions: SharedSubOrderRow[];
  whatsub_b2b_shared_subscriptions_aggregate: { aggregate: { count: number } };
}

export async function getSharedSubOrders(
  filters: SharedSubOrderFilters,
  limit: number,
  offset: number,
  token: string
) {
  const query = `
    query GetSharedSubscriptionOrders(
      $_eq: uuid!, $limit: Int, $offset: Int,
      $where: whatsub_b2b_shared_subscriptions_bool_exp!
    ) {
      whatsub_b2b_shared_subscriptions(
        where: { _and: [{ user_id: { _eq: $_eq }}, $where] }
        limit: $limit offset: $offset
        order_by: { created_at: desc }
      ) {
        created_at allocated_at id payment_id plan_id price short_id status
        whatsub_plan {
          plan_name
          whatsub_service { service_name image_url }
        }
      }
      whatsub_b2b_shared_subscriptions_aggregate(
        where: { _and: [{ user_id: { _eq: $_eq }}, $where] }
      ) { aggregate { count } }
    }
  `;
  const data = await graphqlRequest<GetOrdersResponse>(
    query,
    { _eq: filters.userId, limit, offset, where: buildInnerWhere(filters) },
    token
  );
  return {
    rows: data.whatsub_b2b_shared_subscriptions,
    total: data.whatsub_b2b_shared_subscriptions_aggregate.aggregate.count,
  };
}

interface GetAllOrdersResponse {
  whatsub_b2b_shared_subscriptions: SharedSubOrderRow[];
}

export async function getAllSharedSubOrders(filters: SharedSubOrderFilters, token: string) {
  const query = `
    query GetAllSharedSubOrders($_eq: uuid!, $where: whatsub_b2b_shared_subscriptions_bool_exp!) {
      whatsub_b2b_shared_subscriptions(
        where: { _and: [{ user_id: { _eq: $_eq }}, $where] }
        order_by: { created_at: desc }
      ) {
        created_at allocated_at id payment_id plan_id price short_id status
        whatsub_plan {
          plan_name
          whatsub_service { service_name image_url }
        }
      }
    }
  `;
  const data = await graphqlRequest<GetAllOrdersResponse>(
    query,
    { _eq: filters.userId, where: buildInnerWhere(filters) },
    token
  );
  return data.whatsub_b2b_shared_subscriptions;
}

interface GetStatusesResponse {
  whatsub_b2b_shared_subscriptions: Array<{ status: string }>;
}

export async function getSharedSubOrderStatuses(userId: string, token: string) {
  const query = `
    query GetUniqueSharedSubscriptionStatuses($_eq: uuid!) {
      whatsub_b2b_shared_subscriptions(
        where: { user_id: { _eq: $_eq } }
        distinct_on: status
      ) { status }
    }
  `;
  const data = await graphqlRequest<GetStatusesResponse>(query, { _eq: userId }, token);
  return data.whatsub_b2b_shared_subscriptions.map((s) => s.status);
}
